import * as React from 'react';
import update from 'immutability-helper';
import { Button, Form, Grid, Input } from 'semantic-ui-react';
import { ErrorMessage } from 'components/error-message';
import { getI18n } from 'services/i18n-service';
import { LandingTemplate } from 'components/landing-template';
import { LoginType } from 'types/login-type';
import { translations } from 'types/translations';

import './styles.css';
import { ForgotPasswordModal } from './forgot-password-modal';

interface InnerState {
  username: string;
  password: string;
  loginErrorMessage?: string;
  loginType: LoginType;
  showForgotPasswordModal: boolean;
  isPasswordResetRequested: boolean;
}

export interface LoginProps {
  loginType?: LoginType;
  username?: string;
  loginErrorMessage?: string;
  isProcessing?: boolean;
  isProcessingPasswordReset: boolean;
  hasEnterpriseLogin: boolean;
  passwordResetSuccess: boolean;
  passwordResetErrorMessage: string;
}

export interface LoginActionProps {
  login: (loginType: LoginType, usernameOrBarcode: string, passwordOrPin: string) => void;
  cancel: () => void;
  resetPassword: (userEmail: string) => void;
}

export type Props = LoginProps & LoginActionProps;

export class LoginPage extends React.Component<Props, InnerState> {

  constructor(props: Props) {
    super(props);

    this.state = {
      username: '',
      password: '',
      loginErrorMessage: '',
      loginType: LoginType.UsernamePassword,
      showForgotPasswordModal: false,
      isPasswordResetRequested: false
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    let username = this.state.username;
    let password = this.state.password;
    let loginErrorMessage = this.state.loginErrorMessage;
    let loginType = nextProps.loginType;
    let isPasswordResetRequested = this.state.isPasswordResetRequested;

    if (nextProps.loginType === LoginType.BarcodePin && nextProps.username) {
      username = nextProps.username;
    }

    if (this.state.loginType === LoginType.UsernamePassword && nextProps.loginType === LoginType.BarcodePin) {
      password = '';
      loginErrorMessage = nextProps.loginErrorMessage;
    } else if (this.state.loginType === LoginType.BarcodePin && nextProps.loginType === LoginType.UsernamePassword) {
      username = '';
      password = '';
      loginErrorMessage = nextProps.loginErrorMessage;
    } else {
      username = username;
      loginErrorMessage = nextProps.loginErrorMessage;
    }

    if (this.props.isProcessingPasswordReset === false && nextProps.isProcessingPasswordReset) {
      isPasswordResetRequested = true;
    }

    this.setState(update(this.state, {
      loginType: { $set: loginType },
      username: { $set: username },
      password: { $set: password },
      loginErrorMessage: { $set: loginErrorMessage },
      isPasswordResetRequested: { $set: isPasswordResetRequested }
    }));
  }

  public render() {
    const i18n = getI18n();

    let usernameFieldLabel: string;
    let passwordFieldLabel: string;

    if (this.state.loginType === LoginType.BarcodePin) {
      usernameFieldLabel = i18n.t(translations.Barcode);
      passwordFieldLabel = i18n.t(translations.Pin);
    } else {
      usernameFieldLabel = i18n.t(translations.Username);
      passwordFieldLabel = i18n.t(translations.Password);

      if (!this.props.hasEnterpriseLogin) {
        usernameFieldLabel += '/' + i18n.t(translations.Email);
      }
    }

    return (
      <div className="container-main login-page landing-page">
        <LandingTemplate>
          <div className="login-form">
            <Form>
              <Form.Field className="username-field">
                <label className="form-label">{usernameFieldLabel}</label>
                <Input name="Username" size="huge" className="login-input no-border-radius"
                  value={this.state.username}
                  onChange={this.setUsername}
                  onInput={this.setUsername} />
              </Form.Field>
              <Form.Field className="password-field">
                <label className="form-label">{passwordFieldLabel}</label>
                <Input name="Password" size="huge" className="login-input no-border-radius"
                  type="password"
                  autoComplete="password"
                  value={this.state.password}
                  onChange={this.setPassword}
                  onInput={this.setPassword} />
              </Form.Field>
              <span className="disclaimer-text">By using OPTEL Product&nbsp;Explorer™, you agree to OPTEL’s Terms and Conditions. Additionally, the information provided by OPTEL Product&nbsp;Explorer™ does not guarantee US custom identification and clearance. </span>
              <Grid stackable={true} reversed="computer tablet" className="login-buttons">
                <Grid.Row columns={2}>
                  <Grid.Column mobile={8} computer={8}
                    floated="right" className="submit-button-column">
                    <Button
                      fluid={true}
                      className="login-button ui neutral button no-border-radius"
                      disabled={!this.canLogin()}
                      loading={this.props.isProcessing}
                      onClick={this.login}>
                      {i18n.t(translations.Login)}
                    </Button>
                  </Grid.Column>
                  <Grid.Column mobile={8} computer={8} className="forgot-password-column">
                    &nbsp;
                    {/* <a href="#" onClick={this.showModal} className="forgot-password-link">{i18n.t(translations.ForgotPassword)}</a>*/}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              <ErrorMessage message={this.props.loginErrorMessage} showErrorPrefix={false} />              
            </Form>
          </div>
        </LandingTemplate>
        <ForgotPasswordModal 
          showForgotPasswordModal={this.state.showForgotPasswordModal}
          isPasswordResetRequested={this.state.isPasswordResetRequested}
          passwordResetSuccess={this.props.passwordResetSuccess}
          passwordResetErrorMessage={this.props.passwordResetErrorMessage}
          isProcessingPasswordReset={this.props.isProcessingPasswordReset}
          hasEnterpriseLogin={this.props.hasEnterpriseLogin}
          resetPassword={this.sendResetPassword}
          closeModal={this.closeModal}
        />
      </div>
    );
  }

  private canLogin = () => {
    return !(this.state.username === '' || this.state.password === '' || this.props.isProcessing);
  }

  private login = () => {
    this.setState(update(this.state, {
      loginErrorMessage: { $set: '' }
    }));
    this.props.login(
      this.state.loginType,
      this.state.username,
      this.state.password
    );
  }

  private sendResetPassword = (userEmail: string) => {
    this.setState(update(this.state, {
      isPasswordResetRequested: { $set: true }
    }));
    this.props.resetPassword(userEmail);
  }

  private setUsername = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState(update(this.state, {
      username: { $set: event.currentTarget.value }
    }));
  }

  private setPassword = (event: React.FormEvent<HTMLInputElement>) => {
    this.setState(update(this.state, {
      password: { $set: event.currentTarget.value }
    }));
  }

  private closeModal = () => {
    this.setState(update(this.state, {
      showForgotPasswordModal: { $set: false },
      isPasswordResetRequested: { $set: false }
    }));
  }

  // private showModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
  //   event.preventDefault();
  //   this.setState(update(this.state, {
  //     showForgotPasswordModal: { $set: true }
  //   }));
  // }
}


// WEBPACK FOOTER //
// ./src/pages/login/component.tsx